import { useState, useEffect } from 'react';
import { Check, Loader2, Boxes } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import { getMetaPages } from '@/services/api';
import type { Page } from '@/types/broadcast';

interface StepSelectPageProps {
    selectedPages: Page[];
    onTogglePage: (page: Page) => void;
    onSetPages: (pages: Page[]) => void;
}

export default function StepSelectPage({ selectedPages, onTogglePage, onSetPages }: StepSelectPageProps) {
    const [pages, setPages] = useState<Page[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeCategoryIds, setActiveCategoryIds] = useState<string[]>([]);
    const [activeGroupIds, setActiveGroupIds] = useState<string[]>([]);

    useEffect(() => {
        fetchPages();
    }, []);

    const fetchPages = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getMetaPages();
            const seen = new Set<string>();
            const unique = data.filter(p => {
                if (seen.has(p.page_id)) return false;
                seen.add(p.page_id);
                return true;
            });
            setPages(unique);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erro desconhecido');
        } finally {
            setLoading(false);
        }
    };

    // Drop ghost pages: entries pre-filled (e.g. via duplicate) that no longer
    // exist in the user's accessible pages would otherwise stay invisible in
    // selectedPages and reach the backend as invalid IDs.
    useEffect(() => {
        if (pages.length === 0) return;
        const validIds = new Set(pages.map(p => p.page_id));
        if (selectedPages.some(p => !validIds.has(p.page_id))) {
            onSetPages(selectedPages.filter(p => validIds.has(p.page_id)));
        }
    }, [pages, selectedPages, onSetPages]);

    const normalize = (str: string) =>
        str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

    const categories = Array.from(new Map(
        pages
            .filter(p => p.internal_category_id && p.internal_category_name)
            .map(p => [p.internal_category_id, p.internal_category_name])
    ).entries()).map(([id, name]) => ({ id, name }));

    const groups = Array.from(new Map(
        pages
            .filter(p => p.internal_group_id && p.internal_group_name)
            .map(p => [p.internal_group_id, p.internal_group_name])
    ).entries()).map(([id, name]) => ({ id, name }));

    const filteredPages = pages.filter(page => {
        const matchesCategory =
            activeCategoryIds.length === 0 ||
            (page.internal_category_id != null && activeCategoryIds.includes(String(page.internal_category_id)));
        if (!matchesCategory) return false;
        const matchesGroup =
            activeGroupIds.length === 0 ||
            (page.internal_group_id != null && activeGroupIds.includes(String(page.internal_group_id)));
        if (!matchesGroup) return false;
        if (!searchTerm.trim()) return true;
        const term = normalize(searchTerm);
        return normalize(page.page_name).includes(term) || page.page_id.includes(searchTerm.trim());
    });

    const isSelected = (pageId: string) => selectedPages.some(p => p.page_id === pageId);

    const handleCategoryChange = (vals: string[]) => {
        const added = vals.filter(id => !activeCategoryIds.includes(id));
        const removed = activeCategoryIds.filter(id => !vals.includes(id));

        let updatedPages = [...selectedPages];

        // Remove páginas das categorias desmarcadas
        if (removed.length > 0) {
            const toRemove = new Set(
                pages
                    .filter(p => p.internal_category_id != null && removed.includes(String(p.internal_category_id)))
                    .map(p => p.page_id)
            );
            updatedPages = updatedPages.filter(p => !toRemove.has(p.page_id));
        }

        // Adiciona páginas das categorias marcadas
        if (added.length > 0) {
            const current = new Set(updatedPages.map(p => p.page_id));
            const toAdd = pages.filter(p =>
                p.internal_category_id != null &&
                added.includes(String(p.internal_category_id)) &&
                !current.has(p.page_id)
            );
            updatedPages = [...updatedPages, ...toAdd];
        }

        setActiveCategoryIds(vals);
        onSetPages(updatedPages);
    };

    const someFilteredSelected = filteredPages.some(p => isSelected(p.page_id));

    const handleToggleAll = () => {
        if (someFilteredSelected) {
            // Desmarca só as que estão visíveis/filtradas
            const filteredIds = new Set(filteredPages.map(p => p.page_id));
            onSetPages(selectedPages.filter(p => !filteredIds.has(p.page_id)));
        } else {
            const currentIds = new Set(selectedPages.map(p => p.page_id));
            const toAdd = filteredPages.filter(p => !currentIds.has(p.page_id));
            onSetPages([...selectedPages, ...toAdd]);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[#7dd3fc] mb-4" />
                <p className="text-gray-600">Carregando suas páginas...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-12">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Erro ao Carregar Páginas</h3>
                <p className="text-gray-600 mb-4">{error}</p>
                <button onClick={fetchPages} className="px-4 py-2 bg-[#0f172a] text-white rounded-lg hover:bg-[#1e293b] transition-colors">
                    Tentar Novamente
                </button>
            </div>
        );
    }

    if (pages.length === 0) {
        return (
            <div className="text-center py-12">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma Página Encontrada</h3>
                <p className="text-gray-600">Você ainda não possui páginas do Facebook conectadas.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Selecione as Páginas</h3>
                <p className="text-sm text-gray-600 mt-2">Escolha quais páginas do Facebook enviarão o broadcast</p>
                {selectedPages.length > 0 && (
                    <div className="mt-3 inline-flex items-center gap-2 bg-[#7dd3fc]/10 text-[#0f172a] px-3 py-1.5 rounded-full text-sm font-medium">
                        <Check className="h-4 w-4" />
                        {selectedPages.length} página{selectedPages.length > 1 ? 's' : ''} selecionada{selectedPages.length > 1 ? 's' : ''}
                    </div>
                )}
            </div>

            {/* Search + Category + Select All */}
            <div className="flex flex-col md:flex-row items-end gap-3">
                <div className="flex-1 w-full space-y-2">
                    <Label htmlFor="broadcast-page-search" className="text-sm font-medium">Buscar Página</Label>
                    <Input
                        id="broadcast-page-search"
                        type="text"
                        autoComplete="off"
                        placeholder="Busque por nome ou ID..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full"
                    />
                </div>
                <div className="w-full md:w-64 space-y-2">
                    <Label className="text-sm font-medium">Filtrar por Categoria</Label>
                    <SearchableMultiSelect
                        options={categories.map(c => ({ value: String(c.id), label: c.name || 'Sem nome' }))}
                        values={activeCategoryIds}
                        onValuesChange={handleCategoryChange}
                        placeholder="Todas as categorias"
                        searchPlaceholder="Buscar categoria..."
                        showTags={false}
                        selectedLabel={n => `${n} categoria${n > 1 ? 's' : ''} selecionada${n > 1 ? 's' : ''}`}
                    />
                </div>
                {groups.length > 0 && (
                    <div className="w-full md:w-64 space-y-2">
                        <Label className="text-sm font-medium">Filtrar por Grupo</Label>
                        <SearchableMultiSelect
                            options={groups.map(g => ({ value: String(g.id), label: g.name || 'Sem nome' }))}
                            values={activeGroupIds}
                            onValuesChange={setActiveGroupIds}
                            placeholder="Todos os grupos"
                            searchPlaceholder="Buscar grupo..."
                            showTags={false}
                            selectedLabel={n => `${n} grupo${n > 1 ? 's' : ''} selecionado${n > 1 ? 's' : ''}`}
                        />
                    </div>
                )}
                <Button variant="outline" size="sm" onClick={handleToggleAll} className="whitespace-nowrap h-9">
                    {someFilteredSelected ? 'Desmarcar Todas' : 'Selecionar Todas'}
                </Button>
            </div>

            {/* Lista de Páginas */}
            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                {filteredPages.length === 0 ? (
                    <div className="text-center py-8">
                        <p className="text-gray-500">Nenhuma página encontrada</p>
                    </div>
                ) : (
                    filteredPages.map(page => {
                        const selected = isSelected(page.page_id);
                        return (
                            <button
                                key={page.page_id}
                                onClick={() => onTogglePage(page)}
                                className={`relative w-full p-4 border-2 rounded-xl text-left transition-all hover:border-[#7dd3fc] hover:shadow-md
                                    ${selected ? 'border-[#7dd3fc] bg-[#7dd3fc]/5 shadow-md' : 'border-gray-200 bg-white'}`}
                            >
                                <div className="flex items-center gap-4">
                                    <div className={`flex-shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors
                                        ${selected ? 'bg-[#7dd3fc] border-[#7dd3fc]' : 'border-gray-300 bg-white'}`}>
                                        {selected && <Check className="h-4 w-4 text-white" />}
                                    </div>
                                    <div className="relative flex-shrink-0">
                                        <div className="w-14 h-14 bg-[#0f172a] rounded-full flex items-center justify-center">
                                            <span className="text-white font-semibold text-base">
                                                {page.page_name.substring(0, 2).toUpperCase()}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h4 className="font-semibold text-gray-900 truncate">{page.page_name}</h4>
                                            {page.internal_category_name && (
                                                <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 uppercase tracking-wider">
                                                    {page.internal_category_name}
                                                </span>
                                            )}
                                            {page.internal_group_name && (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-100 uppercase tracking-wider">
                                                    <Boxes className="h-2.5 w-2.5" />
                                                    {page.internal_group_name}
                                                    {page.internal_group_app_name && (
                                                        <span className="text-indigo-500 normal-case">→ {page.internal_group_app_name}</span>
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500 truncate">ID: {page.page_id}</p>
                                        {page.meta_user_name && (
                                            <p className="text-xs text-gray-400 truncate">Conta: {page.meta_user_name}</p>
                                        )}
                                    </div>
                                </div>
                            </button>
                        );
                    })
                )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                    <strong>Dica:</strong> Será criado 1 broadcast por página selecionada.
                </p>
            </div>
        </div>
    );
}
